import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppTimeField } from '../../components/AppTimeField';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { getLocations } from '../../services/locationService';
import {
  createShift,
  getShiftDetails,
  updateShift,
} from '../../services/shiftService';
import type { ShiftDetails } from '../../types/shift';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import {
  isConcurrencyConflict,
  shiftWorkPolicyErrorMessage,
} from '../../utils/shiftWorkPolicyErrors';
import {
  matchingSpreadMinutes,
  minutesFromFormValue,
  parseNonNegativeDuration,
  parsePositiveDuration,
} from '../../utils/workDuration';
import { DURATION_INPUT_HINT, formatMinutes } from '../../utils/dateTime';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { WorkDurationFields } from '../../components/WorkDurationFields';
import { runOperation } from '../../utils/operationFeedback';

type CreateProps = NativeStackScreenProps<RootStackParamList, 'ShiftCreate'>;
type EditProps = NativeStackScreenProps<RootStackParamList, 'ShiftEdit'>;
type Props = CreateProps | EditProps;

type FormState = {
  shiftCode: string;
  shiftName: string;
  description: string;
  locationId: number | null;
  locationLabel: string | null;
  shiftStartTime: string;
  shiftEndTime: string;
  crossesMidnight: boolean;
  expectedWorkMinutes: string;
  lunchBreakMinutes: string;
  spreadMinutes: string;
  minimumHalfDayMinutes: string;
  checkInOpenMinutes: string;
  checkInCloseMinutes: string;
  checkOutOpenMinutes: string;
  checkOutCloseMinutes: string;
  lateGraceMinutes: string;
  earlyExitGraceMinutes: string;
  autoCheckoutEnabled: boolean;
  autoCheckoutAfterMinutes: string;
  isFlexibleShift: boolean;
  isDefault: boolean;
  isActive: boolean;
};

const EMPTY: FormState = {
  shiftCode: '',
  shiftName: '',
  description: '',
  locationId: null,
  locationLabel: null,
  shiftStartTime: '09:00',
  shiftEndTime: '18:00',
  crossesMidnight: false,
  expectedWorkMinutes: '',
  lunchBreakMinutes: '',
  spreadMinutes: '',
  minimumHalfDayMinutes: '',
  checkInOpenMinutes: formatMinutes(60),
  checkInCloseMinutes: formatMinutes(120),
  checkOutOpenMinutes: formatMinutes(60),
  checkOutCloseMinutes: formatMinutes(120),
  lateGraceMinutes: formatMinutes(10),
  earlyExitGraceMinutes: formatMinutes(10),
  autoCheckoutEnabled: false,
  autoCheckoutAfterMinutes: '',
  isFlexibleShift: false,
  isDefault: false,
  isActive: true,
};

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

function durationOrEmpty(value: number | null | undefined): string {
  if (value == null) return '';
  return formatMinutes(value);
}

function fromDetails(item: ShiftDetails): FormState {
  return {
    shiftCode: item.shiftCode,
    shiftName: item.shiftName,
    description: item.description ?? '',
    locationId: item.locationId,
    locationLabel: item.locationName,
    shiftStartTime: item.shiftStartTime,
    shiftEndTime: item.shiftEndTime,
    crossesMidnight: item.crossesMidnight,
    expectedWorkMinutes: formatMinutes(item.expectedWorkMinutes),
    lunchBreakMinutes: formatMinutes(item.lunchBreakMinutes),
    spreadMinutes: formatMinutes(item.spreadMinutes),
    minimumHalfDayMinutes: formatMinutes(item.minimumHalfDayMinutes),
    checkInOpenMinutes: formatMinutes(item.checkInOpenMinutes),
    checkInCloseMinutes: formatMinutes(item.checkInCloseMinutes),
    checkOutOpenMinutes: formatMinutes(item.checkOutOpenMinutes),
    checkOutCloseMinutes: formatMinutes(item.checkOutCloseMinutes),
    lateGraceMinutes: formatMinutes(item.lateGraceMinutes),
    earlyExitGraceMinutes: formatMinutes(item.earlyExitGraceMinutes),
    autoCheckoutEnabled: item.autoCheckoutEnabled,
    autoCheckoutAfterMinutes: durationOrEmpty(item.autoCheckoutAfterMinutes),
    isFlexibleShift: item.isFlexibleShift,
    isDefault: item.isDefault,
    isActive: item.isActive !== false,
  };
}

export function validateShiftForm(
  form: FormState,
  isEdit: boolean,
): string | null {
  if (!isEdit && !form.shiftCode.trim()) return 'Shift code is required.';
  if (form.shiftCode.trim().length > 50) return 'Shift code is too long.';
  if (!form.shiftName.trim()) return 'Shift name is required.';
  if (form.shiftName.trim().length > 150) return 'Shift name is too long.';
  if (form.description.trim().length > 500) {
    return 'Description is too long.';
  }
  if (!TIME_PATTERN.test(form.shiftStartTime.trim())) {
    return 'Select a valid start time.';
  }
  if (!TIME_PATTERN.test(form.shiftEndTime.trim())) {
    return 'Select a valid end time.';
  }

  const expected = parsePositiveDuration(
    form.expectedWorkMinutes,
    'Expected work duration',
  );
  if (typeof expected === 'string') return expected;
  if (form.lunchBreakMinutes.trim() === '') {
    return 'Lunch break duration is required.';
  }
  const lunch = parseNonNegativeDuration(
    form.lunchBreakMinutes,
    'Lunch break duration',
  );
  if (typeof lunch === 'string') return lunch;
  if (form.spreadMinutes.trim() === '') {
    return 'Spread duration is required.';
  }
  const spread = parsePositiveDuration(form.spreadMinutes, 'Spread duration');
  if (typeof spread === 'string') return spread;
  if (!matchingSpreadMinutes(expected, lunch, spread)) {
    return 'Spread duration must equal expected work duration plus lunch break duration.';
  }
  if (spread > 24 * 60) {
    return 'Spread duration cannot exceed 24 hours.';
  }
  const halfDay = parsePositiveDuration(
    form.minimumHalfDayMinutes,
    'Minimum half day',
  );
  if (typeof halfDay === 'string') return halfDay;
  if (halfDay > expected) {
    return 'Minimum half day cannot exceed expected work duration.';
  }

  for (const [value, label] of [
    [form.checkInOpenMinutes, 'Check-in open'],
    [form.checkInCloseMinutes, 'Check-in close'],
    [form.checkOutOpenMinutes, 'Check-out open'],
    [form.checkOutCloseMinutes, 'Check-out close'],
    [form.lateGraceMinutes, 'Late grace'],
    [form.earlyExitGraceMinutes, 'Early exit grace'],
  ] as const) {
    const parsed = parseNonNegativeDuration(value, label);
    if (typeof parsed === 'string') return parsed;
  }

  if (form.autoCheckoutEnabled) {
    const after = parsePositiveDuration(
      form.autoCheckoutAfterMinutes,
      'Auto checkout after',
    );
    if (typeof after === 'string') return after;
  }

  return null;
}

function isEditRoute(props: Props): props is EditProps {
  return props.route.name === 'ShiftEdit';
}

export function ShiftFormScreen(props: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      readOnlyLabel: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      readOnlyValue: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error },
      switchLabel: { ...typography.body, color: palette.textPrimary },
      hint: { ...typography.caption, color: palette.textSecondary },
    }),
    [palette],
  );

  const isEdit = isEditRoute(props);
  const shiftId = isEdit ? props.route.params.shiftId : null;
  const navigation = props.navigation;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [details, setDetails] = useState<ShiftDetails | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savingLock = useRef(false);

  const load = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const data = await getShiftDetails(shiftId);
      setDetails(data);
      setForm(fromDetails(data));
      dirty.current = false;
      setError(null);
    } catch (caught) {
      setError(
        shiftWorkPolicyErrorMessage(
          caught,
          'The requested shift could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    if (isEdit) load().catch(() => undefined);
  }, [isEdit, load]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm(current => ({ ...current, [key]: value }));
  }

  const loadLocationOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getLocations({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items.map(item => ({
        id: item.locationId,
        label: item.locationName,
        subtitle: item.locationCode,
      }));
    },
    [],
  );

  function confirmLeave() {
    if (!dirty.current) {
      navigation.goBack();
      return;
    }
    Alert.alert(
      'Discard changes?',
      'You have unsaved changes. Leave without saving?',
      [
        { text: 'Stay', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ],
    );
  }

  async function submit() {
    if (savingLock.current) return;
    const validation = validateShiftForm(form, isEdit);
    if (validation) {
      setFieldError(validation);
      return;
    }

    savingLock.current = true;
    setSaving(true);
    setFieldError(null);
    setError(null);

    const payload = {
      locationId: form.locationId,
      shiftName: form.shiftName,
      description: form.description,
      shiftStartTime: form.shiftStartTime,
      shiftEndTime: form.shiftEndTime,
      crossesMidnight: form.crossesMidnight,
      expectedWorkMinutes: minutesFromFormValue(form.expectedWorkMinutes) ?? 0,
      lunchBreakMinutes: minutesFromFormValue(form.lunchBreakMinutes) ?? 0,
      minimumHalfDayMinutes:
        minutesFromFormValue(form.minimumHalfDayMinutes) ?? 0,
      checkInOpenMinutes: minutesFromFormValue(form.checkInOpenMinutes) ?? 0,
      checkInCloseMinutes: minutesFromFormValue(form.checkInCloseMinutes) ?? 0,
      checkOutOpenMinutes: minutesFromFormValue(form.checkOutOpenMinutes) ?? 0,
      checkOutCloseMinutes:
        minutesFromFormValue(form.checkOutCloseMinutes) ?? 0,
      lateGraceMinutes: minutesFromFormValue(form.lateGraceMinutes) ?? 0,
      earlyExitGraceMinutes:
        minutesFromFormValue(form.earlyExitGraceMinutes) ?? 0,
      autoCheckoutEnabled: form.autoCheckoutEnabled,
      autoCheckoutAfterMinutes: form.autoCheckoutEnabled
        ? minutesFromFormValue(form.autoCheckoutAfterMinutes)
        : null,
      isFlexibleShift: form.isFlexibleShift,
      isDefault: form.isDefault,
    };

    try {
      if (isEdit && shiftId && details) {
        await runOperation('update', () => updateShift(shiftId, {
          ...payload,
          displayOrder: details.displayOrder,
          isActive: form.isActive,
          rowVersion: details.rowVersion,
        }));
      } else {
        await runOperation('add', () => createShift({
          ...payload,
          shiftCode: form.shiftCode,
        }));
      }
      dirty.current = false;
      navigation.goBack();
    } catch (caught) {
      if (isConcurrencyConflict(caught) && isEdit) {
        const message = shiftWorkPolicyErrorMessage(caught);
        await load();
        setError(message);
      } else {
        setError(
          shiftWorkPolicyErrorMessage(
            caught,
            isEdit ? 'Unable to update shift.' : 'Unable to create shift.',
          ),
        );
      }
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  }

  const permission = isEdit
    ? PERMISSIONS.SHIFT_UPDATE
    : PERMISSIONS.SHIFT_CREATE;

  return (
    <PermissionGuard permission={permission} fallback={<UnauthorizedScreen />}>
      {loading ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title={isEdit ? 'Edit shift' : 'Create shift'}
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading shift" />
        </AppScreen>
      ) : error && isEdit && !details ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit shift"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title={isEdit ? 'Edit shift' : 'Create shift'}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {isEdit ? (
              <>
                <Text style={themed.readOnlyLabel}>Shift code</Text>
                <Text style={themed.readOnlyValue}>{form.shiftCode}</Text>
              </>
            ) : (
              <AppInput
                label="Shift code *"
                value={form.shiftCode}
                onChangeText={value => setField('shiftCode', value)}
                autoCapitalize="characters"
              />
            )}
            <AppInput
              label="Shift name *"
              value={form.shiftName}
              onChangeText={value => setField('shiftName', value)}
            />
            <AppInput
              label="Description"
              value={form.description}
              onChangeText={value => setField('description', value)}
            />
            <SearchableEntitySelector
              label="Location"
              selectedValue={form.locationId}
              selectedLabel={form.locationLabel}
              loadOptions={loadLocationOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: option.id,
                  locationLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: null,
                  locationLabel: null,
                }));
              }}
              placeholder="Optional location"
            />
            <AppTimeField
              label="Start time *"
              value={form.shiftStartTime}
              onChange={value => setField('shiftStartTime', value)}
            />
            <AppTimeField
              label="End time *"
              value={form.shiftEndTime}
              onChange={value => setField('shiftEndTime', value)}
            />
            <WorkDurationFields
              expectedWorkMinutes={form.expectedWorkMinutes}
              lunchBreakMinutes={form.lunchBreakMinutes}
              spreadMinutes={form.spreadMinutes}
              onExpectedChange={value =>
                setField('expectedWorkMinutes', value)
              }
              onLunchChange={value => setField('lunchBreakMinutes', value)}
              onSpreadChange={value => setField('spreadMinutes', value)}
              hintStyle={themed.hint}
            />
            <AppInput
              label="Minimum half day *"
              value={form.minimumHalfDayMinutes}
              onChangeText={value => setField('minimumHalfDayMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Late grace"
              value={form.lateGraceMinutes}
              onChangeText={value => setField('lateGraceMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Early exit grace"
              value={form.earlyExitGraceMinutes}
              onChangeText={value => setField('earlyExitGraceMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Check-in open"
              value={form.checkInOpenMinutes}
              onChangeText={value => setField('checkInOpenMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Check-in close"
              value={form.checkInCloseMinutes}
              onChangeText={value => setField('checkInCloseMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Check-out open"
              value={form.checkOutOpenMinutes}
              onChangeText={value => setField('checkOutOpenMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <AppInput
              label="Check-out close"
              value={form.checkOutCloseMinutes}
              onChangeText={value => setField('checkOutCloseMinutes', value)}
              autoCapitalize="none"
              autoCorrect={false}
              placeholder={DURATION_INPUT_HINT}
            />
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Crosses midnight</Text>
              <Switch
                value={form.crossesMidnight}
                onValueChange={value => setField('crossesMidnight', value)}
              />
            </View>
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Auto checkout</Text>
              <Switch
                value={form.autoCheckoutEnabled}
                onValueChange={value => setField('autoCheckoutEnabled', value)}
              />
            </View>
            {form.autoCheckoutEnabled ? (
              <AppInput
                label="Auto checkout after *"
                value={form.autoCheckoutAfterMinutes}
                onChangeText={value =>
                  setField('autoCheckoutAfterMinutes', value)
                }
                autoCapitalize="none"
                autoCorrect={false}
                placeholder={DURATION_INPUT_HINT}
              />
            ) : null}
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Flexible shift</Text>
              <Switch
                value={form.isFlexibleShift}
                onValueChange={value => setField('isFlexibleShift', value)}
              />
            </View>
            <View style={styles.switchRow}>
              <Text style={themed.switchLabel}>Default shift</Text>
              <Switch
                value={form.isDefault}
                onValueChange={value => setField('isDefault', value)}
              />
            </View>
            {isEdit ? (
              <View style={styles.switchRow}>
                <Text style={themed.switchLabel}>Active</Text>
                <Switch
                  value={form.isActive}
                  onValueChange={value => setField('isActive', value)}
                />
              </View>
            ) : null}
            {fieldError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {fieldError}
              </Text>
            ) : null}
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton title="Save" onPress={submit} loading={saving} />
            <AppButton
              title="Cancel"
              variant="secondary"
              onPress={confirmLeave}
            />
          </AppCard>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.lg },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
